import { useCallback, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import TimeAgo from 'react-timeago';
import { useEvalSetStatus } from '../hooks/useEvalSetStatus';
import { useInvalidateJobs } from '../hooks/useJobs';
import { RunHeader } from '../components/RunHeader';
import { StopRunDialog, type StopKind } from '../components/StopRunDialog';
import { ContentSkeleton } from '../components/ContentSkeleton';
import { Chevron } from '../components/Chevron';
import { useStopRun } from '../hooks/useStopRun';
import type {
  EvalSetStatus,
  EventBrief,
  JobStatus,
  MetricsQueryResult,
  SamplePhase,
  SampleStatus,
  StatusProgress,
  TaskStatus,
  WaitingSample,
} from '../types/monitoring';
import { isTerminal } from '../types/monitoring';
import {
  admissionDiagnostics,
  dotClassForStatus,
  friendlyTroubleReason,
  isStatusUnavailable,
  labelForStatus,
} from '../utils/jobStatus';
import '../index.css';

export default function EvalSetStatusPage() {
  const { evalSetId } = useParams<{ evalSetId: string }>();
  const result = useEvalSetStatus({ evalSetId: evalSetId ?? null });

  // Mobile scrolls the whole page so both headers leave room for the review.
  // Desktop keeps the run header above the scrolling status content.
  return (
    <div className="pane flex flex-col md:h-full md:overflow-hidden">
      <RunHeader evalSetId={evalSetId ?? ''} current="status" />
      <div className="md:min-h-0 md:flex-1 md:overflow-y-auto">
        {result.state === 'loading' && (
          <div className="p-4">
            <ContentSkeleton message="Loading status…" />
          </div>
        )}
        {result.state === 'error' && (
          <p className="p-4 text-sm text-err">{result.message}</p>
        )}
        {result.state === 'ready' && <StatusBody data={result.data} />}
      </div>
    </div>
  );
}

function StatusBody({ data }: { data: EvalSetStatus }) {
  // Fields backed by Pydantic default_factory are optional in the OpenAPI
  // schema even though the server always sends them — default here.
  const waiting = data.waiting_samples ?? [];
  const tasks = data.tasks ?? [];
  const errorSources = Object.keys(data.errors ?? {});
  return (
    <div>
      <StatusHeader data={data} />
      {errorSources.length > 0 && (
        <p className="callout callout-warn mx-4 mt-3">
          Some data could not be loaded ({errorSources.join(', ')}); showing
          what we have. Status may be incomplete.
        </p>
      )}
      {waiting.length > 0 && <WaitingSamplesCard samples={waiting} />}
      {tasks.length > 0
        ? tasks.map(task => <TaskCard key={task.log_file} task={task} />)
        : waiting.length === 0 && <EmptyTasks data={data} />}
    </div>
  );
}

function WaitingSamplesCard({ samples }: { samples: WaitingSample[] }) {
  return (
    <section className="border-b border-line">
      <div className="flex flex-wrap items-baseline gap-x-2 bg-warn-tint px-4 py-2 text-xs">
        <span className="font-semibold text-warn">
          Waiting for Kubernetes · {samples.length}
        </span>
        <span className="text-warn/80">
          sandbox pods not yet scheduled; these samples have not started
        </span>
      </div>
      <table className="w-full text-left text-xs">
        <thead className="eyebrow">
          <tr>
            <th className="px-4 py-1.5 font-semibold">Sample</th>
            <th className="px-2 py-1.5 font-semibold">Pod</th>
            <th className="px-2 py-1.5 font-semibold">Reason</th>
          </tr>
        </thead>
        <tbody>
          {samples.map(s => (
            <tr key={s.pod_name} className="border-t border-line align-top">
              <td className="px-4 py-2 font-mono text-ink">
                {s.sample_id ?? '—'}
              </td>
              <td className="px-2 py-2 font-mono text-muted">{s.pod_name}</td>
              <td className="px-2 py-2">
                <span
                  className={`font-medium ${s.reason ? 'text-err' : 'text-muted'}`}
                >
                  {s.reason
                    ? (friendlyTroubleReason(s.reason) ?? s.reason)
                    : 'scheduling'}
                </span>
                {s.detail && (
                  <div className="max-w-xl text-[11px] text-muted">
                    {s.detail}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function EmptyTasks({ data }: { data: EvalSetStatus }) {
  const pods = data.pods?.pods ?? [];
  const hasRunner = pods.some(p => p.component === 'runner');
  const sandboxPending = pods.filter(
    p => p.component === 'sandbox' && p.phase === 'Pending'
  ).length;

  let message: string;
  if (data.status === 'complete' || data.status === 'failed') {
    message = 'No tasks were recorded for this eval set.';
  } else if (sandboxPending > 0) {
    message = `Waiting for Kubernetes — ${sandboxPending} sandbox pod${
      sandboxPending === 1 ? '' : 's'
    } pending. Samples appear once they start.`;
  } else if (hasRunner) {
    message =
      'Runner starting up (installing task packages). Tasks appear once the first one begins.';
  } else if (isStatusUnavailable(data.status, data.admission)) {
    message = 'Task status is currently unavailable.';
  } else {
    message = 'Waiting for the runner pod to start…';
  }
  return <p className="px-4 py-4 text-sm text-muted">{message}</p>;
}

function StatusHeader({ data }: { data: EvalSetStatus }) {
  const trouble = friendlyTroubleReason(data.trouble_reason);
  const admission = admissionDiagnostics(data.admission);
  return (
    <section className="border-b border-line px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-ink">
          <span aria-hidden="true" className={dotClassForStatus(data.status)} />
          {labelForStatus(data.status, data.admission)}
        </span>
        {trouble && <span className="text-xs text-warn">{trouble}</span>}
        {admission.map(detail => (
          <span key={detail} className="text-xs text-muted">
            {detail}
          </span>
        ))}
        {data.created_by && (
          <span className="text-xs text-muted">by {data.created_by}</span>
        )}
        {!isTerminal(data.status) && (
          <Link
            to={`/eval-set/${encodeURIComponent(data.job_id)}/approvals`}
            className="btn btn-ghost min-h-11 no-underline"
          >
            Review approvals
            {(data.pending_interactions ?? 0) > 0 &&
              ` (${data.pending_interactions} waiting)`}
          </Link>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <RunnerHealth metrics={data.metrics ?? null} />
          {data.is_owner_or_admin && (
            <OwnerActions jobId={data.job_id} status={data.status} />
          )}
        </div>
      </div>
      <div className="mt-2.5">
        <ProgressBar progress={data.progress} />
        <ProgressLegend progress={data.progress} />
      </div>
    </section>
  );
}

function OwnerActions({ jobId, status }: { jobId: string; status: JobStatus }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const invalidateJobs = useInvalidateJobs();
  const [open, setOpen] = useState(false);
  const terminal = isTerminal(status);

  const onDone = useCallback(
    (kind: StopKind) => {
      setOpen(false);
      // The Jobs list may hold a cached page from moments ago; it must not
      // come back showing this run as still running.
      invalidateJobs();
      if (kind === 'force') navigate('/jobs');
      else
        void queryClient.invalidateQueries({
          queryKey: ['eval-set-status', jobId],
        });
    },
    [invalidateJobs, jobId, navigate, queryClient]
  );
  const stop = useStopRun(jobId, onDone);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={terminal ? 'btn btn-ghost text-err' : 'btn btn-ghost'}
        title={
          terminal
            ? 'The run has finished; tear down what it still holds in Kubernetes.'
            : undefined
        }
      >
        {terminal ? 'Force stop…' : 'Stop…'}
      </button>
      <StopRunDialog
        // Remount when the run turns terminal so the dialog opens on Force stop.
        key={terminal ? 'force' : 'stop'}
        jobId={jobId}
        open={open}
        terminal={terminal}
        busy={stop.busy}
        error={stop.error}
        onConfirm={kind => void stop.confirm(kind)}
        onClose={() => {
          setOpen(false);
          stop.reset();
        }}
      />
    </>
  );
}

function RunnerHealth({
  metrics,
}: {
  metrics: Record<string, MetricsQueryResult> | null;
}) {
  if (!metrics) return null;
  const cpu = metrics['runner_cpu']?.value ?? null;
  const mem = metrics['runner_memory']?.value ?? null;
  const sandboxPods = metrics['sandbox_pods']?.value ?? null;
  const sandboxGpus = metrics['sandbox_gpus']?.value ?? null;
  const parts: string[] = [];
  if (cpu != null) parts.push(`CPU ${formatCpu(cpu)}`);
  if (mem != null) parts.push(`mem ${formatBytes(mem)}`);
  if (sandboxPods != null) parts.push(`${sandboxPods} sandbox pods`);
  if (sandboxGpus != null && sandboxGpus > 0) parts.push(`${sandboxGpus} GPUs`);
  if (parts.length === 0) return null;
  return (
    <span className="text-xs text-muted">runner: {parts.join(' · ')}</span>
  );
}

const PROGRESS_COLORS = {
  completed: 'bg-neutral-mark',
  running: 'bg-ok-mark',
  errored: 'bg-err-mark',
  limit: 'bg-warn-mark',
  canceled: 'bg-line-strong',
  waiting: 'bg-line',
};

function ProgressBar({ progress }: { progress: StatusProgress }) {
  const total = progress.total || 1;
  // Same language as the status text: green is current activity, amber/red
  // are attention, finished and not-yet-started work are grays.
  const segments: { value: number; className: string }[] = [
    { value: progress.completed, className: PROGRESS_COLORS.completed },
    { value: progress.running, className: PROGRESS_COLORS.running },
    { value: progress.errored, className: PROGRESS_COLORS.errored },
    { value: progress.limit, className: PROGRESS_COLORS.limit },
    { value: progress.canceled, className: PROGRESS_COLORS.canceled },
    { value: progress.waiting, className: PROGRESS_COLORS.waiting },
  ];
  return (
    <div
      className="flex h-2 w-full overflow-hidden rounded-full bg-tint"
      role="img"
      aria-label={`${progress.completed} of ${progress.total} complete`}
    >
      {segments.map((seg, i) =>
        seg.value > 0 ? (
          <div
            key={i}
            className={seg.className}
            style={{ width: `${(seg.value / total) * 100}%` }}
          />
        ) : null
      )}
    </div>
  );
}

function ProgressLegend({ progress }: { progress: StatusProgress }) {
  return (
    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted">
      <LegendItem
        dot={PROGRESS_COLORS.completed}
        label="completed"
        value={progress.completed}
      />
      <LegendItem
        dot={PROGRESS_COLORS.running}
        label="running"
        value={progress.running}
      />
      <LegendItem
        dot={PROGRESS_COLORS.errored}
        label="errored"
        value={progress.errored}
      />
      <LegendItem
        dot={PROGRESS_COLORS.limit}
        label="limit"
        value={progress.limit}
      />
      {progress.canceled > 0 && (
        <LegendItem
          dot={PROGRESS_COLORS.canceled}
          label="canceled"
          value={progress.canceled}
        />
      )}
      <LegendItem
        dot={PROGRESS_COLORS.waiting}
        label="waiting"
        value={progress.waiting}
      />
      <span className="font-medium text-ink-2">{progress.total} total</span>
    </div>
  );
}

function LegendItem({
  dot,
  label,
  value,
}: {
  dot: string;
  label: string;
  value: number;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
      {label} {value}
    </span>
  );
}

function TaskCard({ task }: { task: TaskStatus }) {
  const [expanded, setExpanded] = useState(false);
  const trouble = friendlyTroubleReason(task.trouble_reason);
  return (
    <section className="border-b border-line">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left hover:bg-hover"
        aria-expanded={expanded}
      >
        <Chevron open={expanded} />
        <span className="min-w-0 break-all text-sm font-medium text-ink">
          {task.task_name}
        </span>
        {task.live && (
          <span
            className="eyebrow text-[10px] text-ok"
            title="This task's log is still being written"
          >
            in progress
          </span>
        )}
        {trouble && <span className="text-xs text-err">{trouble}</span>}
        <span className="ml-auto w-28 shrink-0 sm:w-40">
          <ProgressBar progress={task.progress} />
        </span>
        <span className="w-12 shrink-0 text-right text-xs tabular-nums text-muted">
          {task.progress.completed}/{task.progress.total}
        </span>
      </button>
      {expanded && <SampleTable samples={task.samples ?? []} />}
    </section>
  );
}

function SampleTable({ samples }: { samples: SampleStatus[] }) {
  if (samples.length === 0) {
    return (
      <p className="border-t border-line px-4 py-2.5 text-xs text-muted">
        No samples tracked yet.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto border-t border-line bg-surface-2">
      <table className="w-full text-left text-xs">
        <thead className="eyebrow">
          <tr>
            <th className="px-4 py-1.5 font-semibold">Sample</th>
            <th className="px-2 py-1.5 font-semibold">Phase</th>
            <th className="px-2 py-1.5 font-semibold">Retries</th>
            <th className="px-2 py-1.5 font-semibold">Tokens</th>
            <th className="px-2 py-1.5 font-semibold">Time</th>
            <th className="px-2 py-1.5 font-semibold">Score</th>
            <th className="px-2 py-1.5 font-semibold">Last event</th>
          </tr>
        </thead>
        <tbody>
          {samples.map(sample => (
            <SampleRow key={`${sample.id}-${sample.epoch}`} sample={sample} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SampleRow({ sample }: { sample: SampleStatus }) {
  const lastEvent = sample.last_events?.at(-1) ?? null;
  const label = `${sample.id}${sample.epoch > 1 ? ` · e${sample.epoch}` : ''}`;
  return (
    <tr className="border-t border-line align-top">
      <td className="px-4 py-2 font-mono text-ink">
        {sample.uuid ? (
          <Link
            to={`/permalink/sample/${encodeURIComponent(sample.uuid)}`}
            className="link font-normal no-underline hover:underline"
          >
            {label}
          </Link>
        ) : (
          label
        )}
      </td>
      <td className="px-2 py-2">
        <PhaseChip phase={sample.phase} limit={sample.limit ?? null} />
      </td>
      <td className="px-2 py-2 tabular-nums">
        {sample.retries == null ? (
          <span className="text-muted">—</span>
        ) : sample.retries > 0 ? (
          <span className="text-warn">{sample.retries}</span>
        ) : (
          <span className="text-muted">0</span>
        )}
      </td>
      <td className="px-2 py-2 whitespace-nowrap tabular-nums text-ink-2">
        {sample.total_tokens != null
          ? sample.total_tokens.toLocaleString()
          : '—'}
      </td>
      <td className="px-2 py-2 whitespace-nowrap tabular-nums text-ink-2">
        {sample.total_time != null
          ? formatDuration(sample.total_time)
          : sample.working_time != null
            ? formatDuration(sample.working_time)
            : '—'}
      </td>
      <td className="px-2 py-2 whitespace-nowrap tabular-nums text-ink-2">
        {sample.latest_score ?? '—'}
      </td>
      <td className="px-2 py-2">
        <LastEvent event={lastEvent} />
      </td>
    </tr>
  );
}

function LastEvent({ event }: { event: EventBrief | null }) {
  if (!event) return <span className="text-muted">—</span>;
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="badge uppercase tracking-wide">{event.type}</span>
        {event.description && (
          <span className="text-ink-2">{event.description}</span>
        )}
        {event.pending && (
          <span className="text-[10px] text-ok">in progress</span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-2 text-[11px] text-muted">
        {event.timestamp && (
          <span title={event.timestamp}>
            <TimeAgo date={event.timestamp} />
          </span>
        )}
        {event.duration_seconds != null && (
          <span>took {formatDuration(event.duration_seconds)}</span>
        )}
      </div>
    </div>
  );
}

// Phase as colored text: green for activity, amber/red for attention, neutral
// for the rest, so a table of finished samples doesn't read as a wall of pills.
const PHASE_CLASSES: Record<SamplePhase, string> = {
  waiting: 'text-muted',
  init: 'text-ink-2',
  running: 'text-ok',
  scoring: 'text-ok',
  awaiting_approval: 'text-warn',
  completed: 'text-ink-2',
  errored: 'text-err',
  limit: 'text-warn',
  canceled: 'text-muted',
};

function PhaseChip({
  phase,
  limit,
}: {
  phase: SamplePhase;
  limit: string | null;
}) {
  const text = phase === 'limit' && limit ? `limit: ${limit}` : phase;
  return (
    <span className={`text-[11px] font-medium ${PHASE_CLASSES[phase]}`}>
      {text}
    </span>
  );
}

function formatCpu(nanocores: number): string {
  const cores = nanocores / 1_000_000_000;
  return `${cores.toFixed(cores < 1 ? 2 : 1)} cores`;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GiB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MiB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KiB`;
  return `${bytes} B`;
}

function formatDuration(seconds: number): string {
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  // Round once to whole seconds, then split — computing minutes and seconds
  // independently can yield "1m 60s" when the seconds part rounds up.
  const total = Math.round(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}m ${secs}s`;
}
